import { PartialWithId } from '@inmagik/react-crud/dist/types'
import classNames from 'classnames'
import { FormikHelpers } from 'formik'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { FiltriBilance } from '../../components/FiltriBilance'
import ModalConfirm from '../../components/ModalConfirm'
import ModalForm from '../../components/ModalForm'
import Paginator from '../../components/Paginator'
import { useBilanceList, useBilancia, usePartialUpdateBilancia } from '../../hooks/bilance'
import { useQsFilters } from '../../hooks/filters'
import { Bilancia } from '../../types'
import { transformErrorForForm, useIsCliente } from '../../utils/utils'
import ModalFiltersBilancia from '../BilanciaDettaglio/ModalFiltersBilancia'
import BilanceTable from './BilanceTable'
import FormBilancia from './FormBilancia'
import ListGroupFilters from './ListGroupFilters'

const initialValues = {
  matricola: '',
  tipo_bilancia: '',
  nome_produttore: '',
  tipo_bilancia_em: '',
  sede: undefined,
  verif_periodica: '',
  cert_approv: '',
  rapporto_taratura: false,
}

const schema = yup.object().shape({
  matricola: yup.string().required().label('Matricola'),
  tipo_bilancia: yup.string().required().label('Tipo bilancia'),
  nome_produttore: yup.string().required().label('Nome produttore'),
  sede: yup.number().required().label('Sede'),
  tipo_bilancia_em: yup.string().required().label('Tipo bilancia'),
  verif_periodica: yup.boolean().required().label('Strumento omologato'),
  portata_massima: yup.number().label('Portata massima'),
  portata_minima: yup.number().label('Portata minima'),
  cert_approv: yup
    .string()
    .label('Provvedimento')
    .when('verif_periodica', (verif_periodica: any, schema: any) => {
      if (verif_periodica[0] !== 'false' && verif_periodica[0] !== false) {
        return schema.required()
      } else {
        return schema.notRequired()
      }
    }),
  // minimo 4 cifre e massimo 4 cifre
  anno_marcatura_ce: yup
    .string()
    .nullable()
    .matches(/^[0-9]{4}$/, 'Deve essere un anno a 4 cifre')
    .label('Anno marcatura CE'),
})

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? 'ragione_sociale',
  tipo_bilancia: params.get('tipo_bilancia') ?? '',
  cliente: params.get('cliente') ?? '',
  sede: params.get('sede') ?? '',
  verif_periodica: params.get('verif_periodica') ?? '',
  rapporto_taratura: params.get('rapporto_taratura') ?? '',
  data_scadenza_periodica__gte: params.get('data_scadenza_periodica__gte') ?? '',
  data_scadenza_periodica__lte: params.get('data_scadenza_periodica__lte') ?? '',
  data_scadenza_taratura__gte: params.get('data_scadenza_taratura__gte') ?? '',
  data_scadenza_taratura__lte: params.get('data_scadenza_taratura__lte') ?? '',
  con_richiesta_periodica: params.get('con_richiesta_periodica') ?? '',
  data_richiesta_periodica__gte: params.get('data_richiesta_periodica__gte') ?? '',
  data_richiesta_periodica__lte: params.get('data_richiesta_periodica__lte') ?? '',
  data_intervento_periodica_aperto__isnull: params.get('data_intervento_periodica_aperto__isnull') ?? '',
  data_intervento_periodica_aperto__gte: params.get('data_intervento_periodica_aperto__gte') ?? '',
  data_intervento_periodica_aperto__lte: params.get('data_intervento_periodica_aperto__lte') ?? '',
  data_intervento_taratura_aperto__isnull: params.get('data_intervento_taratura_aperto__isnull') ?? '',
  data_intervento_taratura_aperto__gte: params.get('data_intervento_taratura_aperto__gte') ?? '',
  data_intervento_taratura_aperto__lte: params.get('data_intervento_taratura_aperto__lte') ?? '',
  fuori_uso: params.get('fuori_uso') ?? '',
  dismessa: params.get('dismessa') ?? '',
  autoprodotta: params.get('autoprodotta') ?? '',
})

export default function Bilance() {
  const isCliente = useIsCliente()
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { data: bilance, isLoading } = useBilanceList({
    search: filters.search,
    ordering: filters.ordering,
    page: String(filters.page),
    tipo_bilancia: filters.tipo_bilancia,
    sede: filters.sede,
    verif_periodica: filters.verif_periodica,
    rapporto_taratura: filters.rapporto_taratura,
    data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
    data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
    data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
    data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
    con_richiesta_periodica: filters.con_richiesta_periodica,
    data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
    data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
    cliente: filters.cliente,
    data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
    data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
    data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
    data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
    data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
    data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
    fuori_uso: filters.fuori_uso,
    dismessa: filters.dismessa,
    autoprodotta: filters.autoprodotta,
  })

  const [openFilters, setOpenFilters] = useState(false)

  const [modalFiltersState, modalFiltersActions] = useModalTrigger()
  const [modalUpdateState, modalUpdateActions] = useModalTrigger<Bilancia>()
  const [modalDeleteState, modalDeleteActions] = useModalTrigger<Bilancia>()

  const { mutateAsync: updatePartialBilanica } = usePartialUpdateBilancia()

  const bilancia = useBilancia(
    String(modalUpdateState.value?.id) || String(modalDeleteState.value?.id),
    modalUpdateState.isOpen || modalDeleteState.isOpen
  )

  const saveBilancia = useCallback(
    (bilancia: PartialWithId<Bilancia>, actions: FormikHelpers<Bilancia>) => {
      delete bilancia.doc_anagrafica_pesa
      delete bilancia.doc_comunicaz_installaz
      delete bilancia.doc_modello_e
      delete bilancia.doc_dichiaraz_ue_om
      delete bilancia.storico_interventi
      return updatePartialBilanica({
        ...bilancia,
        sede: bilancia?.sede,
        matricole_celle: bilancia?.matricole_celle_content?.join(' '),
      })
        .then((bilancia) => {
          actions.resetForm()
          modalUpdateActions.close()
          toast.success('Bilancia aggiornata con successo', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          })
        })
        .catch((e) => {
          actions.setErrors(transformErrorForForm(e))
        })
    },
    [updatePartialBilanica, modalUpdateActions]
  )

  const areFiltersActive = useMemo(() => {
    const { page, search, ordering, ...rest } = filters
    return Object.values(rest).some((v) => v !== '')
  }, [filters])

  const filtersListGroupRef = useRef<HTMLDivElement>(null)

  // click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersListGroupRef.current && !filtersListGroupRef.current.contains(event.target as Node)) {
        setOpenFilters(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [filtersListGroupRef])

  return (
    <div className="page page-list">
      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-between list-header">
          <div className="d-flex align-items-center">
            <h1 className="title">Bilance</h1>
            {isLoading && <Spinner animation="border" role="status" className="ms-4" variant="secondary" size="sm" />}
          </div>
          {!isCliente && (
            <div>
              <Link
                to="/bilance/crea"
                className=" d-flex align-items-center btn btn-secondary text-white"
                type="button"
              >
                <span className="btn-label">Crea bilancia</span>
                <span className="material-icons ms-2">add</span>
              </Link>
            </div>
          )}
        </div>
        <div className="col-12">
          <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar">
            <div className="ms-2 d-flex align-items-center list-filters list-filters-inline">
              <div className="position-relative">
                <input
                  type="text"
                  style={{ width: '300px', paddingLeft: '40px' }}
                  className="form-control"
                  placeholder="Cerca per matricola..."
                  value={uiFilters.search}
                  onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
                />
                <span className="position-absolute material-icons" style={{ left: '10px', top: '8px' }}>
                  search
                </span>
              </div>
              <div className="ms-2 position-relative">
                <Button
                  onClick={() => {
                    setOpenFilters(!openFilters)
                  }}
                  variant={openFilters ? 'secondary' : 'outline-secondary'}
                  className={classNames('d-flex align-items-center', {
                    'text-white': openFilters,
                  })}
                  type="button"
                >
                  <span
                    className="material-icons-outlined"
                    style={{
                      fontSize: 17,
                    }}
                  >
                    filter_alt
                  </span>
                  <span className="ms-2 btn-label">Filtri</span>
                </Button>
                <div className="position-absolute" ref={filtersListGroupRef}>
                  {openFilters && (
                    <ListGroupFilters
                      filters={filters}
                      setFilters={setFilters}
                      modalFiltersActions={modalFiltersActions}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="me-2 list-count">
              Stai visualizzando {bilance?.count ?? 0} di {bilance?.full_count ?? 0} bilance
            </div>
          </div>
          {areFiltersActive && (
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar">
              <div className="d-flex align-items-center flex-wrap gap-2 active-filters">
                <FiltriBilance filters={filters} setFilters={setFilters} />
              </div>
              <div>
                <Button
                  onClick={() => {
                    setFilters(initFilters(new URLSearchParams()))
                  }}
                  size="sm"
                  variant="danger"
                  className="d-flex align-items-center"
                  aria-label="Reset filtri"
                  type="button"
                >
                  <span
                    className="material-icons-outlined me-2"
                    style={{
                      fontSize: 17,
                    }}
                  >
                    filter_alt_off
                  </span>
                  <span className="btn-label">Reset</span>
                </Button>
              </div>
            </div>
          )}
          <BilanceTable
            bilance={bilance}
            isLoading={isLoading}
            setFilters={setFilters}
            uiFilters={uiFilters}
            hasFiltersActive={areFiltersActive}
            modalActionsForm={modalUpdateActions}
            modalActionsDelete={modalDeleteActions}
          />
          <div className="position-relative">
            <Paginator
              count={bilance?.count ?? 0}
              currentPage={uiFilters.page}
              goToPage={(page) => setFilters({ page })}
            />
          </div>
        </div>
      </div>
      {modalFiltersState.isOpen && (
        <ModalFiltersBilancia
          isOpen={modalFiltersState.isOpen}
          toggle={() => {
            modalFiltersActions.close()
            setOpenFilters(false)
          }}
          setFilters={setFilters}
          filters={filters}
        />
      )}
      {modalUpdateState.isOpen && (
        <ModalForm<Bilancia>
          isOpen={modalUpdateState.isOpen}
          toggle={modalUpdateActions.close}
          title="Modifica bilancia"
          initialValues={
            bilancia?.data
              ? {
                  ...bilancia.data,
                  matricole_celle_content: bilancia.data.matricole_celle.split(/\s+/g),
                }
              : initialValues
          }
          schema={schema}
          save={saveBilancia}
          labelSave="Salva"
        >
          <FormBilancia />
        </ModalForm>
      )}
      {modalDeleteState.isOpen && (
        <ModalConfirm
          isOpen={modalDeleteState.isOpen}
          toggle={modalDeleteActions.close}
          title="Elimina bilancia"
          confirmText="Elimina"
          body={'Sei sicuro di voler eliminare la bilancia ' + modalDeleteState.value?.matricola + '?'}
          action={async () => {
            await updatePartialBilanica({
              ...modalDeleteState.value,
              cancellato: true,
            }).then(() => {
              modalDeleteActions.close()
              toast.success('Bilancia eliminata con successo', {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              })
            })
          }}
        />
      )}
    </div>
  )
}
